import { api } from "../http";
import type { DocumentRequirementDefinition, Page, UpsertDocumentRequirementDefinition } from "../types";

const BASE_PATH = "/api/admin/document-requirements";

const normalizeDefinition = (input: any): DocumentRequirementDefinition => ({
  id: String(input?.id ?? input?.uuid ?? ""),
  code: String(input?.code ?? "").toUpperCase(),
  name: input?.name ?? input?.label ?? "",
  description: input?.description ?? null,
  active: Boolean(input?.active ?? input?.enabled ?? true),
  updatedAt: input?.updatedAt ?? input?.modifiedAt ?? input?.createdAt ?? null,
  updatedBy: input?.updatedBy ?? input?.modifiedBy ?? input?.createdBy ?? null,
});

const extractDefinitions = (payload: any): DocumentRequirementDefinition[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    return payload.map(normalizeDefinition);
  }
  const page = payload as Page<any>;
  if (Array.isArray(page?.content)) {
    return page.content.map(normalizeDefinition);
  }
  if (Array.isArray((payload as any)?.data?.content)) {
    return (payload as any).data.content.map(normalizeDefinition);
  }
  if (Array.isArray((payload as any)?.items)) {
    return (payload as any).items.map(normalizeDefinition);
  }
  return [];
};

export const documentRequirementsApi = {
  async list(page = 0, size = 200): Promise<DocumentRequirementDefinition[]> {
    const response = await api.get<Page<DocumentRequirementDefinition> | DocumentRequirementDefinition[] | any>(
      `${BASE_PATH}?page=${page}&size=${size}`,
    );
    return extractDefinitions(response);
  },

  async listActive(): Promise<DocumentRequirementDefinition[]> {
    const response = await api.get<DocumentRequirementDefinition[] | any>(`${BASE_PATH}/active`);
    return extractDefinitions(response);
  },

  async get(id: string): Promise<DocumentRequirementDefinition> {
    const response = await api.get<DocumentRequirementDefinition | any>(`${BASE_PATH}/${id}`);
    return normalizeDefinition(response);
  },

  create(payload: UpsertDocumentRequirementDefinition) {
    return api.post<DocumentRequirementDefinition, UpsertDocumentRequirementDefinition>(BASE_PATH, payload);
  },

  update(id: string, payload: UpsertDocumentRequirementDefinition) {
    return api.put<DocumentRequirementDefinition, UpsertDocumentRequirementDefinition>(`${BASE_PATH}/${id}`, payload);
  },

  remove(id: string) {
    return api.delete<void>(`${BASE_PATH}/${id}`);
  },
};
